import { useEffect, useMemo, useState } from 'react';

import { useRepositorySkillFiles, useRepositorySkillVersions } from '../../hooks/use-skills-repository';
import { ResizableListPane } from '../../shell/ResizableListPane';
import { mono, primaryButton, secondaryButton } from '../proxy-vault/ui';
import { buildTree, SkillTreeNode } from '../skills/SkillTreeNode';

import { RepositoryFileViewer } from './RepositoryFileViewer';
import { SkillVersionList } from './SkillVersionList';

import type { RepositorySkill } from '../../lib/api-client';

interface Props {
  skill: RepositorySkill;
  onNewVersion: () => void;
  onSetCurrent: (versionId: string) => Promise<void>;
  onDelete: () => Promise<void>;
}

/**
 * One skill: header (name · id · description · actions), then three columns —
 * version history, the viewed version's file tree, and the file preview.
 * The viewed version defaults to the current one and follows it when a new
 * version is published or the pointer moves.
 */
export function SkillRepositoryDetail({ skill, onNewVersion, onSetCurrent, onDelete }: Props) {
  const currentVersionId = skill.current_version?.id ?? null;
  const [viewedVersionId, setViewedVersionId] = useState<string | null>(currentVersionId);
  const [deleting, setDeleting] = useState(false);

  // Follow the current version whenever it changes (publish / rollback / other skill selected).
  useEffect(() => {
    setViewedVersionId(currentVersionId);
  }, [skill.id, currentVersionId]);

  const history = useRepositorySkillVersions(skill.id, `${currentVersionId ?? ''}:${skill.updated_at}`);
  const browser = useRepositorySkillFiles(skill.id, viewedVersionId);

  const tree = useMemo(
    () =>
      buildTree(
        browser.files.map((f) => ({ path: f.path, type: 'file' as const, size: f.size })),
        skill.name,
      ),
    [browser.files, skill.name],
  );
  const viewedVersion = history.versions.find((v) => v.id === viewedVersionId) ?? skill.current_version;

  const remove = async (): Promise<void> => {
    if (!confirm(`Delete "${skill.name}"? Conversations that selected it lose it on their next turn. Its history is kept.`)) return;
    setDeleting(true);
    try {
      await onDelete();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div
        data-testid="skrepo-detail-header"
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          flexShrink: 0,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{skill.name}</span>
            <span style={{ fontSize: 11, color: 'var(--text-mute)', fontFamily: mono }}>{skill.id}</span>
            {skill.current_version && (
              <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>current v{skill.current_version.version}</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-soft)', marginTop: 4, lineHeight: 1.5 }}>
            {skill.description || 'No description in SKILL.md.'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-mute)', marginTop: 4, fontFamily: mono }}>
            tenant {skill.tenant_id} · by {skill.created_by ?? '—'}
          </div>
        </div>
        <button type="button" data-testid="skrepo-new-version" style={primaryButton} onClick={onNewVersion}>
          New version
        </button>
        <button
          type="button"
          data-testid="skrepo-delete"
          style={{ ...secondaryButton, color: 'var(--kind-failure)' }}
          disabled={deleting}
          onClick={() => void remove()}
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <ResizableListPane
          storageKey="skills-repository-versions"
          defaultWidth={300}
          minWidth={220}
          maxWidth={480}
          paneStyle={{ borderRight: '1px solid var(--border)', overflowY: 'auto', background: 'var(--bg-alt)' }}
        >
          <SectionLabel>Versions</SectionLabel>
          <SkillVersionList
            versions={history.versions}
            status={history.status}
            error={history.error}
            hasMore={history.hasMore}
            onLoadMore={history.loadMore}
            currentVersionId={currentVersionId}
            viewedVersionId={viewedVersionId}
            onView={setViewedVersionId}
            onSetCurrent={onSetCurrent}
          />
        </ResizableListPane>

        <ResizableListPane
          storageKey="skills-repository-files"
          defaultWidth={260}
          minWidth={180}
          maxWidth={480}
          paneStyle={{ borderRight: '1px solid var(--border)', overflowY: 'auto', background: 'var(--bg-alt)', padding: '0 0 8px' }}
        >
          <SectionLabel>Files{viewedVersion ? ` (v${viewedVersion.version})` : ''}</SectionLabel>
          {browser.filesStatus === 'loading' && (
            <div style={{ padding: 12, fontSize: 12, color: 'var(--text-mute)' }}>Loading files…</div>
          )}
          {browser.filesStatus === 'error' && (
            <div style={{ padding: 12, fontSize: 12, color: 'var(--kind-failure)' }}>
              {browser.filesError ?? 'Failed to load files.'}
            </div>
          )}
          {browser.filesStatus === 'success' && browser.files.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: 'var(--text-mute)' }}>No files in this version.</div>
          )}
          {tree.map((node) => (
            <SkillTreeNode
              key={node.path}
              node={node}
              depth={0}
              selectedPath={browser.selectedPath}
              onSelectFile={browser.setSelectedPath}
            />
          ))}
        </ResizableListPane>

        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', background: 'var(--surface)' }}>
          <RepositoryFileViewer
            entry={browser.entry}
            content={browser.content}
            status={browser.fileStatus}
            error={browser.fileError}
          />
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '10px 12px 6px',
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        color: 'var(--text-mute)',
        position: 'sticky',
        top: 0,
        background: 'var(--bg-alt)',
        zIndex: 1,
      }}
    >
      {children}
    </div>
  );
}
